import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLang } from '../contexts/LanguageContext';
import { listingsAPI, metaAPI } from '../lib/api';
import ListingCard from '../components/ListingCard';
import { Search, SlidersHorizontal, X, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

export default function Listings() {
  const { t } = useLang();
  const [searchParams, setSearchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [brands, setBrands] = useState([]);
  const [countries, setCountries] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    vehicle_type: searchParams.get('vehicle_type') || '',
    brand: searchParams.get('brand') || '',
    min_price: searchParams.get('min_price') || '',
    max_price: searchParams.get('max_price') || '',
    min_year: searchParams.get('min_year') || '',
    max_year: searchParams.get('max_year') || '',
    country: searchParams.get('country') || '',
    fuel_type: searchParams.get('fuel_type') || '',
    transmission: searchParams.get('transmission') || '',
    sort_by: searchParams.get('sort_by') || 'newest',
  });

  useEffect(() => {
    Promise.all([
      metaAPI.getBrands().then(r => setBrands(r.data.brands)),
      metaAPI.getCountries().then(r => setCountries(r.data.countries)),
    ]).catch(() => {});
  }, []);

  const fetchListings = async (pg = 1) => {
    setLoading(true);
    try {
      const params = { page: pg, limit: 20 };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await listingsAPI.getAll(params);
      setListings(data.listings);
      setTotal(data.total);
      setPages(data.pages);
      setPage(data.page);
    } catch {
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchListings(1); }, []);

  const handleFilter = () => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    setSearchParams(params);
    fetchListings(1);
    setShowFilters(false);
  };

  const resetFilters = () => {
    const reset = { search: '', vehicle_type: '', brand: '', min_price: '', max_price: '', min_year: '', max_year: '', country: '', fuel_type: '', transmission: '', sort_by: 'newest' };
    setFilters(reset);
    setSearchParams({});
    setTimeout(() => fetchListings(1), 0);
  };

  const updateFilter = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));
  const vehicleTypes = ['cars_suvs', 'trucks_buses', 'caravans_campers', 'motorcycles_bikes', 'industrial', 'car_transport', 'yachts_boats'];
  const fuelTypes = ['petrol', 'diesel', 'electric', 'hybrid', 'lpg', 'cng'];
  const transmissions = ['manual', 'automatic'];

  const FilterPanel = () => (
    <div className="space-y-4" data-testid="filter-panel">
      <div>
        <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8] mb-1.5 block">{t('hero.search')}</label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#475569]" />
          <Input value={filters.search} onChange={e => updateFilter('search', e.target.value)} className="pl-9 bg-[#0B0F19] border-[#1E293B] text-[#F1F5F9]" data-testid="filter-search" />
        </div>
      </div>
      <div>
        <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8] mb-1.5 block">{t('filters.type')}</label>
        <Select value={filters.vehicle_type} onValueChange={v => updateFilter('vehicle_type', v === 'all' ? '' : v)}>
          <SelectTrigger data-testid="filter-vehicle-type"><SelectValue placeholder={t('filters.allTypes')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('filters.allTypes')}</SelectItem>
            {vehicleTypes.map(vt => <SelectItem key={vt} value={vt}>{t(`categories.${vt}`)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8] mb-1.5 block">{t('filters.brand')}</label>
        <Input value={filters.brand} onChange={e => updateFilter('brand', e.target.value)} className="bg-[#0B0F19] border-[#1E293B] text-[#F1F5F9]" data-testid="filter-brand" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8] mb-1.5 block">{t('filters.minPrice')}</label>
          <Input type="number" value={filters.min_price} onChange={e => updateFilter('min_price', e.target.value)} className="bg-[#0B0F19] border-[#1E293B] text-[#F1F5F9]" data-testid="filter-min-price" />
        </div>
        <div>
          <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8] mb-1.5 block">{t('filters.maxPrice')}</label>
          <Input type="number" value={filters.max_price} onChange={e => updateFilter('max_price', e.target.value)} className="bg-[#0B0F19] border-[#1E293B] text-[#F1F5F9]" data-testid="filter-max-price" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8] mb-1.5 block">{t('filters.minYear')}</label>
          <Input type="number" value={filters.min_year} onChange={e => updateFilter('min_year', e.target.value)} className="bg-[#0B0F19] border-[#1E293B] text-[#F1F5F9]" data-testid="filter-min-year" />
        </div>
        <div>
          <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8] mb-1.5 block">{t('filters.maxYear')}</label>
          <Input type="number" value={filters.max_year} onChange={e => updateFilter('max_year', e.target.value)} className="bg-[#0B0F19] border-[#1E293B] text-[#F1F5F9]" data-testid="filter-max-year" />
        </div>
      </div>
      <div>
        <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8] mb-1.5 block">{t('filters.country')}</label>
        <Select value={filters.country} onValueChange={v => updateFilter('country', v === 'all' ? '' : v)}>
          <SelectTrigger data-testid="filter-country"><SelectValue placeholder={t('filters.allCountries')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('filters.allCountries')}</SelectItem>
            {countries.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8] mb-1.5 block">{t('filters.fuel')}</label>
        <Select value={filters.fuel_type} onValueChange={v => updateFilter('fuel_type', v === 'all' ? '' : v)}>
          <SelectTrigger data-testid="filter-fuel"><SelectValue placeholder={t('filters.allFuels')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('filters.allFuels')}</SelectItem>
            {fuelTypes.map(f => <SelectItem key={f} value={f}>{t(`fuel_types.${f}`)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8] mb-1.5 block">{t('filters.transmission')}</label>
        <Select value={filters.transmission} onValueChange={v => updateFilter('transmission', v === 'all' ? '' : v)}>
          <SelectTrigger data-testid="filter-transmission"><SelectValue placeholder={t('filters.allTransmissions')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('filters.allTransmissions')}</SelectItem>
            {transmissions.map(tr => <SelectItem key={tr} value={tr}>{t(`transmission_types.${tr}`)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8] mb-1.5 block">{t('filters.sort')}</label>
        <Select value={filters.sort_by} onValueChange={v => updateFilter('sort_by', v)}>
          <SelectTrigger data-testid="filter-sort"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">{t('filters.newest')}</SelectItem>
            <SelectItem value="price_asc">{t('filters.priceAsc')}</SelectItem>
            <SelectItem value="price_desc">{t('filters.priceDesc')}</SelectItem>
            <SelectItem value="year_desc">{t('filters.yearDesc')}</SelectItem>
            <SelectItem value="year_asc">{t('filters.yearAsc')}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex gap-2 pt-2">
        <Button onClick={handleFilter} className="flex-1 bg-[#0A3A82] text-white hover:bg-[#082F6B]" data-testid="apply-filters-btn">{t('filters.apply')}</Button>
        <Button variant="outline" onClick={resetFilters} className="flex-1 border-[#1E293B] text-[#94A3B8] hover:bg-[#1E293B]" data-testid="reset-filters-btn">{t('filters.reset')}</Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0B0F19]" data-testid="listings-page">
      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-[#F1F5F9] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('nav.listings')}
            </h1>
            <p className="text-sm text-[#94A3B8] mt-1">{total} {t('stats.listings').toLowerCase()}</p>
          </div>
          <Button variant="outline" className="md:hidden gap-2 border-[#1E293B] text-[#94A3B8]" onClick={() => setShowFilters(!showFilters)} data-testid="toggle-filters-btn">
            <SlidersHorizontal className="w-4 h-4" />
            {showFilters ? <X className="w-4 h-4" /> : t('filters.type')}
          </Button>
        </div>

        <div className="flex gap-6">
          <aside className="hidden md:block w-72 shrink-0">
            <div className="bg-[#141B2D] rounded-xl border border-[#1E293B] p-5 sticky top-20">
              <h3 className="text-sm font-semibold text-[#F1F5F9] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <SlidersHorizontal className="w-4 h-4 inline mr-2" />Filters
              </h3>
              <FilterPanel />
            </div>
          </aside>

          {showFilters && (
            <div className="fixed inset-0 z-50 bg-black/70 md:hidden" onClick={() => setShowFilters(false)}>
              <div className="absolute right-0 top-0 bottom-0 w-80 bg-[#141B2D] p-5 overflow-y-auto" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-[#F1F5F9]">Filters</h3>
                  <button onClick={() => setShowFilters(false)}><X className="w-5 h-5 text-[#94A3B8]" /></button>
                </div>
                <FilterPanel />
              </div>
            </div>
          )}

          <div className="flex-1">
            {loading ? (
              <div className="flex items-center justify-center py-20" data-testid="loading-spinner">
                <Loader2 className="w-8 h-8 animate-spin text-[#FADB5F]" />
              </div>
            ) : listings.length === 0 ? (
              <div className="text-center py-20" data-testid="no-results">
                <p className="text-lg text-[#94A3B8]">{t('common.noResults')}</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="listings-grid">
                  {listings.map(listing => (
                    <ListingCard key={listing.id} listing={listing} />
                  ))}
                </div>
                {pages > 1 && (
                  <div className="flex justify-center gap-2 mt-8" data-testid="pagination">
                    {Array.from({ length: pages }, (_, i) => i + 1).map(p => (
                      <Button
                        key={p}
                        variant={p === page ? "default" : "outline"}
                        size="sm"
                        className={p === page ? "bg-[#0A3A82] text-white" : "border-[#1E293B] text-[#94A3B8]"}
                        onClick={() => fetchListings(p)}
                        data-testid={`page-${p}`}
                      >{p}</Button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
